<template>
  <div
    v-if="toShowNavItems"
    class="oms-shell-chrome"
    :class="{ 'oms-shell-chrome--collapsed': sidebarCollapsed }"
  >
    <aside class="oms-sidebar" aria-label="Mission Control navigation">
      <div class="oms-sidebar__brand">
        <div class="oms-sidebar__brand-row">
          <span @click="goHome" class="oms-brand-link primary-clr pointer bold">
            <img
              src="../../assets/v_logo_new.png"
              alt="Trukkin"
            />
          </span>

          <v-btn
            icon
            small
            class="oms-sidebar-toggle"
            :title="sidebarCollapsed ? 'Expand sidebar' : 'Collapse sidebar'"
            :aria-label="sidebarCollapsed ? 'Expand sidebar' : 'Collapse sidebar'"
            :aria-expanded="sidebarCollapsed ? 'false' : 'true'"
            @click="toggleSidebar"
          >
            <v-icon>
              {{ sidebarCollapsed ? "chevron_right" : "chevron_left" }}
            </v-icon>
          </v-btn>
        </div>

        <div class="oms-brand-copy">
          <span>Mission Control</span>
        </div>
      </div>

      <div
        class="oms-sidebar__country"
        @mouseenter="cancelWorkingCountryMenuClose"
        @mouseleave="scheduleWorkingCountryMenuClose"
      >
        <v-select
          ref="workingCountrySelect"
          :items="workingCountries"
          v-model="selectedCountry"
          item-value="value"
          class="country-menu-header oms-country-select oms-country-select--sidebar"
          attach
          :menu-props="{
            contentClass: 'oms-country-menu',
            dark: true,
            maxHeight: 280,
            offsetY: true,
            transition: 'slide-y-transition',
          }"
          multiple
          box
          item-text="value"
          @click:append="toggleWorkingCountryMenu"
          @change="setCountry()"
          placeholder="Working Country"
        >
          <template slot="selection" slot-scope="data">
            <span v-if="data.index === 0" class="oms-country-selection">
              <img
                v-if="getWorkingCountryFlag(data.item)"
                :src="getWorkingCountryFlag(data.item)"
                class="oms-country-flag oms-country-flag--selection"
                :class="{
                  'oms-country-flag--round':
                    getWorkingCountryValue(data.item) === 'UAE LA',
                }"
                alt=""
              />
              {{ data.item.value || data.item }}
              <span v-if="selectedCountry.length > 1">
                +{{ selectedCountry.length - 1 }}
              </span>
            </span>
          </template>
          <template slot="item" slot-scope="data">
            <v-list-tile
              v-bind="data.tile.props"
              v-on="data.tile.on"
              class="oms-country-menu__item"
              :class="{
                'oms-country-menu__item--selected': isWorkingCountrySelected(
                  data.item
                ),
              }"
            >
              <v-list-tile-action class="oms-country-menu__action">
                <span
                  class="oms-country-menu__checkbox"
                  :class="{
                    'oms-country-menu__checkbox--selected': isWorkingCountrySelected(
                      data.item
                    ),
                  }"
                >
                  <v-icon v-if="isWorkingCountrySelected(data.item)" small>
                    check
                  </v-icon>
                </span>
              </v-list-tile-action>
              <v-list-tile-content>
                <v-list-tile-title class="oms-country-menu__label">
                  <img
                    v-if="getWorkingCountryFlag(data.item)"
                    :src="getWorkingCountryFlag(data.item)"
                    class="oms-country-flag"
                    :class="{
                      'oms-country-flag--round':
                        getWorkingCountryValue(data.item) === 'UAE LA',
                    }"
                    alt=""
                  />
                  {{ getWorkingCountryValue(data.item) }}
                </v-list-tile-title>
              </v-list-tile-content>
            </v-list-tile>
          </template>
        </v-select>
      </div>

      <div class="oms-sidebar__section-label">Operations</div>
      <nav class="oms-sidebar__nav">
        <v-btn
          v-permissions="'jobs/true'"
          class="oms-side-nav-btn"
          :class="{ 'oms-side-nav-btn--active': index === 2 }"
          flat
          to="/jobs"
          @click="selectActive(2)"
        >
          <v-icon>work_outline</v-icon>
          <span>Jobs</span>
        </v-btn>
        <v-btn
          v-permissions="'procurements/true'"
          class="oms-side-nav-btn"
          :class="{ 'oms-side-nav-btn--active': index === 13 }"
          flat
          to="/procurements"
          @click="selectActive(13)"
        >
          <v-icon>add_task</v-icon>
          <span>Procurements</span>
        </v-btn>
        <v-btn
          v-permissions="'sales/true'"
          class="oms-side-nav-btn"
          :class="{ 'oms-side-nav-btn--active': index === 12 }"
          flat
          to="/sales"
          @click="selectActive(12)"
        >
          <v-icon>schedule</v-icon>
          <span>Sales</span>
        </v-btn>
        <v-btn
          v-permissions="'tender/true'"
          class="oms-side-nav-btn"
          :class="{ 'oms-side-nav-btn--active': index === 25 }"
          flat
          to="/tender/Bid"
          @click="selectActive(25)"
        >
          <v-icon>playlist_add_check</v-icon>
          <span>Tenders</span>
        </v-btn>
        <v-btn
          v-permissions="'request-callback/true'"
          class="oms-side-nav-btn"
          :class="{ 'oms-side-nav-btn--active': index === 6 }"
          flat
          to="/request-callback"
          @click="selectActive(6)"
        >
          <v-icon>restore</v-icon>
          <span>Req. Callbacks</span>
        </v-btn>
        <v-btn
          v-permissions="'mapping-management/true'"
          class="oms-side-nav-btn"
          flat
          target="_blank"
          @click="checkPermissionMapping()"
        >
          <v-icon>map</v-icon>
          <span>Mapping</span>
        </v-btn>
      </nav>

      <div class="oms-sidebar__section-label">Finance</div>
      <nav class="oms-sidebar__nav">
        <v-btn
          to="/accounts"
          v-permissions="'accounts/true'"
          class="oms-side-nav-btn"
          :class="{ 'oms-side-nav-btn--active': index === 17 }"
          flat
          @click="selectActive(17)"
        >
          <v-icon>account_balance_wallet</v-icon>
          <span>Accounts</span>
        </v-btn>
      </nav>

      <div class="oms-sidebar__spacer"></div>

      <div v-if="adminName || adminRole" class="oms-sidebar__user">
        <span class="oms-sidebar__user-avatar" aria-hidden="true">
          {{ adminInitials }}
        </span>
        <span class="oms-sidebar__user-copy">
          <span class="oms-sidebar__user-name">{{ adminName || "User" }}</span>
          <span v-if="adminRole" class="oms-sidebar__user-role">
            {{ adminRole }}
          </span>
        </span>
      </div>
    </aside>

    <header
      class="oms-main-topbar"
      :class="{ 'oms-main-topbar--page-context': hasOpsEntryTopbar }"
    >
      <div
        v-if="hasOpsEntryTopbar"
        class="oms-topbar-page-context"
      >
        <v-btn
          fab
          flat
          class="oms-topbar-back"
          title="Back"
          aria-label="Back"
          @click="goBackFromTopbar"
        >
          <v-icon>arrow_back</v-icon>
        </v-btn>
        <div class="oms-topbar-page-copy">
          <div class="oms-topbar-page-title">{{ opsEntryTopbar.title }}</div>
          <div class="oms-topbar-page-subtitle">
            {{ opsEntryTopbar.subtitle }}
          </div>
        </div>
      </div>

      <div class="oms-topbar-actions">
        <div v-if="unreadCount">
          <v-avatar
            v-if="
              unreadCount != 0 ||
              (jobCommentsUEA > 0 && selectedCountry.indexOf('UAE') >= 0) ||
              (jobCommentsPAK > 0 && selectedCountry.indexOf('PAK') >= 0) ||
              (jobCommentsKSA > 0 && selectedCountry.indexOf('KSA') >= 0) ||
              (jobCommentsUAELA > 0 && selectedCountry.indexOf('UAE LA') >= 0)
            "
            class="oms-notification-count"
            color="orange"
            size="22"
          >
            <span v-if="unreadCount > 99" class="white--text text-h5">99+</span>
            <span v-else-if="unreadCount > 0" class="white--text text-h5">
              {{ unreadCount }}
            </span>
            <span v-else class="white--text text-h5"></span>
          </v-avatar>
        </div>
        <NotificationTray
          :selectedCountry="selectedCountry"
          :jobCommentsKSA="jobCommentsKSA"
          :jobCommentsPAK="jobCommentsPAK"
          :jobCommentsUEA="jobCommentsUEA"
          :jobCommentsUAELA="jobCommentsUAELA"
          @removeDot="notificationRead()"
          :count="parseInt(unreadCount)"
          @unread-count="getUnreadNotificationCount()"
        />

        <v-menu transition="slide-x-transition" offset-y class="profile-drop">
          <v-btn slot="activator" icon small class="oms-topbar-icon-btn">
            <v-icon>supervisor_account</v-icon>
          </v-btn>
          <v-list class="oms-action-menu" style="width: 150px">
            <v-list-tile
              v-permissions="'drivers/true'"
              to="/drivers"
              @click="selectActive(3)"
            >
              <v-list-tile-title>Drivers</v-list-tile-title>
            </v-list-tile>
            <v-list-tile
              v-permissions="'transporter/true'"
              to="/transporter"
              @click="selectActive(16)"
            >
              <v-list-tile-title>Transporters</v-list-tile-title>
            </v-list-tile>
            <v-list-tile
              v-permissions="'customers/true'"
              to="/customers"
              @click="selectActive(7)"
            >
              <v-list-tile-title>Customers</v-list-tile-title>
            </v-list-tile>
            <v-list-tile
              flat
              to="/analytics"
              @click="
                selectActive(30);
                mixpanel();
              "
            >
              <v-list-tile-title>Analytics</v-list-tile-title>
            </v-list-tile>
            <v-list-tile
              to="/user-management"
              v-permissions="'user-management/true'"
              @click="selectActive(8)"
            >
              <v-list-tile-title>Users</v-list-tile-title>
            </v-list-tile>
            <v-list-tile
              to="/role-management"
              v-permissions="'role-management/true'"
              @click="selectActive(8)"
            >
              <v-list-tile-title>Roles</v-list-tile-title>
            </v-list-tile>
            <v-list-tile
              v-permissions="'shipment-documents/true'"
              to="/shipment-documents"
              @click="selectActive(50)"
            >
              <v-list-tile-title>Shipment Docs</v-list-tile-title>
            </v-list-tile>
          </v-list>
        </v-menu>

        <v-menu transition="slide-x-transition" offset-y class="profile-drop">
          <v-btn slot="activator" icon small class="oms-topbar-icon-btn">
            <v-icon>settings</v-icon>
          </v-btn>
          <v-list class="oms-action-menu">
            <v-list-tile
              v-permissions="'milestone/true'"
              to="/milestone"
              @click="selectActive(10)"
            >
              <v-list-tile-title>Milestones</v-list-tile-title>
            </v-list-tile>
            <v-list-tile
              to="/additionalCharge"
              @click="selectActive(60)"
              v-permissions="'additional-charge-name/true'"
            >
              <v-list-tile-title>Additional Charges</v-list-tile-title>
            </v-list-tile>
            <v-list-tile to="/dashboard" @click="selectActive(15)">
              <v-list-tile-title>Booking Stats</v-list-tile-title>
            </v-list-tile>
            <v-list-tile
              v-permissions="'approvals/true'"
              to="/approvals"
              @click="
                selectActive(35);
                refresh();
              "
            >
              <v-list-tile-title>
                Approvals
                <v-avatar color="orange" v-if="newJob || newInquiry" size="18">
                  <v-icon size="15" dark>notifications</v-icon>
                </v-avatar>
              </v-list-tile-title>
            </v-list-tile>
            <v-list-tile
              v-permissions="'gps-devices/true'"
              to="/gps-devices"
              @click="selectActive(17)"
            >
              <v-list-tile-title>GPS Devices</v-list-tile-title>
            </v-list-tile>
            <v-list-tile to="/credit-limit-report">
              <v-list-tile-title>Credit Limit Report</v-list-tile-title>
            </v-list-tile>
            <v-list-tile @click="open">
              <v-list-tile-title>Logout</v-list-tile-title>
            </v-list-tile>
          </v-list>
        </v-menu>
        <v-avatar
          v-if="newJob || newInquiry"
          class="oms-action-dot"
          color="orange"
          size="18"
        >
          <v-icon size="5" dark>dot</v-icon>
        </v-avatar>
      </div>
    </header>

    <v-snackbar
      :timeout="6000"
      top
      color="red darken-2"
      v-model="error"
      class="white--text"
      v-if="error"
      >{{ error }}</v-snackbar
    >

    <v-dialog
      v-model="dialog"
      :max-width="options.width"
      :style="{ zIndex: options.zIndex }"
      @keydown.esc="cancel"
    >
      <v-card>
        <v-toolbar dark :color="options.color" dense flat>
          <v-toolbar-title class="white--text">{{ title }}</v-toolbar-title>
        </v-toolbar>
        <v-card-text v-show="!!message" class="pa-4">{{ message }}</v-card-text>
        <v-card-actions class="pt-0">
          <v-spacer></v-spacer>
          <v-btn color="primary darken-1" text @click.native="agree">Yes</v-btn>
          <v-btn color="grey" text @click.native="cancel">Cancel</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </div>
</template>

<script>
import { StorageKeys } from "../../constants/constants";
import { EventBus } from "../../event-bus.js";
import { notificationList } from "@/constants/api-urls.js";
import removeLocalstorageMixin from "@/mixins/removeLocalstorageMixin";
import checkPermissionsMixin from "@/mixins/checkPermissionsMixin";
import NotificationTray from "./NotificationTray.vue";
import Swal from "sweetalert2";
import moment from "moment";
import { mixins } from "vue-chartjs";
export default {
  mixins: [checkPermissionsMixin, removeLocalstorageMixin],
  created() {
    this.applySidebarState();
    this.unreadCount = localStorage.getItem("unreadCount");

    delete this.axios.defaults.headers.common["token"];
    let token = localStorage.getItem(StorageKeys.SessionKey);
    let config = {
      headers: {
        authorization: `${token}`,
      },
    };
    this.u = localStorage.getItem("user") ? localStorage.getItem("user") : "";
    this.t = config.headers.authorization ? config.headers.authorization : "";

    EventBus.$on("updateCountryGlobally", (obj) => {
      this.selectedCountry = [];
      this.workingCountries.forEach((key, index) => {
        if (key.value === obj.country) {
          this.selectedCountry.push(key.value);
          key.isSelected = true;
        } else {
          key.isSelected = false;
        }
      });
      localStorage.setItem(
        "workingCountries",
        JSON.stringify(this.workingCountries)
      );

      this.$router.push(obj.redirect);
    });
    this.$eventBus.$on("unread-notify-count", (val) => {
      this.getUnreadNotificationCount();
    });

    this.$eventBus.$on("show-nav-items", (val) => {
      this.toShowNavItems = val;
      this.adminName = localStorage.getItem("user");
      this.adminRole = localStorage.getItem("role");
    });

    EventBus.$on("setCountries", (val) => {
      this.workingCountries = val;
      this.selectedCountry = [];
      this.selectedCountry = [val[0].value];
      this.notificationListing = [];
      this.jobComments = 0;
    });
    EventBus.$on("setCountriesfromUrl", (val) => {
      this.notificationListing = [];
      this.jobComments = 0;
      this.selectedCountry = [];
      this.selectedCountry = val;
    });
    if (localStorage.getItem("workingCountries")) {
      this.workingCountries = JSON.parse(
        localStorage.getItem("workingCountries")
      );
      this.selectedCountry = [];
      this.workingCountries.forEach((key, index) => {
        if (key.isSelected) {
          this.selectedCountry.push(key.value);
        }
      });
    }

    this.$eventBus.$on("hide-navbar", () => {
      this.show = false;
    });

    EventBus.$on("logged-in-success", (data) => {
      this.startSocket();
      //console.log("logged in successful------------------------------------");
      this.adminName = data.name;
      this.adminRole = data.roleType;
      delete this.axios.defaults.headers.common["token"];
      let token = localStorage.getItem(StorageKeys.SessionKey);
      let config = {
        headers: {
          authorization: `${token}`,
        },
      };
      this.u = localStorage.getItem("user");
      this.t = config.headers.authorization;
      this.getUnreadNotificationCount();
    });
    EventBus.$on("in-waybill", () => {
      this.toShowNavItems = false;
    });
    this.startSocket();
    //
  },
  beforeDestroy() {
    this.$eventBus.$off("countryChanged");
    this.cancelWorkingCountryMenuClose();
    if (typeof document !== "undefined") {
      document.documentElement.classList.remove("oms-sidebar-collapsed");
    }
  },
  watch: {
    selectedCountry(val) {},
    online() {
      if (!navigator.onLine) {
        Swal.fire({
          title: "Oops!",
          text: "Your internet connection seems to be offline.Please try again.",
          type: "error",
          allowOutsideClick: false,
          confirmButtonColor: "orange",
          confirmButtonText: "Okay",
        }).then((result) => {
          if (result.value) {
          }
        });
      }
    },
  },
  computed: {
    opsEntryTopbar() {
      const routeName = this.$route && this.$route.name;
      if (routeName === "jobs-profile-new") {
        return {
          title: "New Job",
          subtitle:
            "Create a job with customer, route, commercials and documents.",
          fallbackPath: "/jobs",
        };
      }
      if (routeName === "add-enquiry") {
        return {
          title: "New Inquiry",
          subtitle:
            "Create an inquiry with customer, shipment, quote and documents.",
          fallbackPath: "/sales",
        };
      }
      if (routeName === "edit-enquiry") {
        return {
          title: "Update Inquiry",
          subtitle:
            "Update inquiry customer, shipment, quote and documents.",
          fallbackPath: "/sales",
        };
      }
      if (routeName === "re-create-enquiry") {
        return {
          title: "Recreate Inquiry",
          subtitle:
            "Recreate an inquiry with customer, shipment, quote and documents.",
          fallbackPath: "/sales",
        };
      }
      if (routeName === "driver-profile-new") {
        return {
          title: "New Driver Profile",
          subtitle:
            "Register a driver with profile, contact, documents and vehicle.",
          fallbackPath: "/drivers",
        };
      }
      if (routeName === "driver-detail-profile") {
        return {
          title: "Edit Driver Profile",
          subtitle:
            "Update driver profile, contact, documents and vehicle details.",
          fallbackPath: "/drivers",
        };
      }
      if (
        routeName === "driver-detail" ||
        routeName === "driver-detail-assignments"
      ) {
        return {
          title: "Driver Details",
          subtitle: "View driver profile and booking history.",
          fallbackPath: "/drivers",
        };
      }
      if (
        routeName === "transporter-driver-detail-profile" ||
        routeName === "transporter-driver-detail-assignments" ||
        routeName === "transporter-driver-detail-earnings"
      ) {
        const { transporterId, _id, country } = this.$route.params;
        return {
          title: "Driver Details",
          subtitle: "View driver profile, bookings and earnings.",
          fallbackPath: "/transporter",
          // Tabs push routes, so history back would cycle through them.
          backPath: `/transporter/driver/${transporterId}/${_id}/${country}`,
        };
      }
      if (routeName === "transporter-profile-new") {
        return {
          title: "New Transporter",
          subtitle:
            "Register transporter company, documents and contact details.",
          fallbackPath: "/transporter",
        };
      }
      // Basic Details and Drivers are tabs of one page; same topbar for both.
      if (
        routeName === "transporter-detail" ||
        routeName === "transporter-driver-detail"
      ) {
        return {
          title: "Edit Transporter",
          subtitle:
            "Update transporter company, documents and contact details.",
          fallbackPath: "/transporter",
          // The Basic Details / Drivers tabs push routes, so history back
          // would bounce between tabs instead of leaving the transporter.
          backPath: "/transporter",
        };
      }
      return null;
    },
    hasOpsEntryTopbar() {
      return !!this.opsEntryTopbar;
    },
    adminInitials() {
      const name = (this.adminName || "").trim();

      if (!name) {
        return "U";
      }

      return name
        .split(/\s+/)
        .slice(0, 2)
        .map((part) => part.charAt(0).toUpperCase())
        .join("");
    },
  },
  components: {
    EventBus,
    NotificationTray,
  },
  data() {
    return {
      opeartionName: "",
      onLine: navigator.onLine ? true : false,
      error: "",
      operationName1: "",
      unreadCount: 0,
      newJob: 0,
      newInquiry: 0,
      jobComments: 0,
      jobCommentsUEA: 0,
      jobCommentsUAELA: 0,
      jobCommentsPAK: 0,
      jobCommentsKSA: 0,
      selectedCountry: [],
      workingCountries: [],
      workingCountry: [],
      notificationListing: [],
      t: null,
      u: null,
      items: [
        { name: "Logout", link: "" },
        { name: "Finance" },
        { name: "Users" },
      ],
      index: Number(localStorage.getItem("activeMenu")) || 2,
      toShowNavItems: true,
      adminName: localStorage.getItem("user"),
      adminRole: localStorage.getItem("role"),
      sidebarCollapsed: false,
      workingCountryCloseTimer: null,
      show: true,
      dialog: false,
      resolve: null,
      reject: null,
      message: null,
      title: null,
      options: {
        color: "#ffc04c",
        width: 290,
        zIndex: 200,
      },
    };
  },
  methods: {
    applySidebarState() {
      if (typeof document === "undefined") {
        return;
      }

      document.documentElement.classList.toggle(
        "oms-sidebar-collapsed",
        this.sidebarCollapsed
      );
    },
    toggleSidebar() {
      this.sidebarCollapsed = !this.sidebarCollapsed;
      this.applySidebarState();
    },
    toggleWorkingCountryMenu() {
      const countrySelect = this.$refs.workingCountrySelect;

      if (!countrySelect) {
        return;
      }

      this.cancelWorkingCountryMenuClose();
      const shouldOpen = !countrySelect.isMenuActive;
      countrySelect.hasMouseDown = false;
      countrySelect.isFocused = shouldOpen;
      countrySelect.isMenuActive = shouldOpen;
    },
    scheduleWorkingCountryMenuClose() {
      this.cancelWorkingCountryMenuClose();
      this.workingCountryCloseTimer = window.setTimeout(() => {
        this.closeWorkingCountryMenu();
      }, 180);
    },
    cancelWorkingCountryMenuClose() {
      if (!this.workingCountryCloseTimer) {
        return;
      }

      window.clearTimeout(this.workingCountryCloseTimer);
      this.workingCountryCloseTimer = null;
    },
    closeWorkingCountryMenu() {
      const countrySelect = this.$refs.workingCountrySelect;

      if (!countrySelect) {
        return;
      }

      countrySelect.hasMouseDown = false;
      countrySelect.isFocused = false;
      countrySelect.isMenuActive = false;
    },
    getWorkingCountryValue(item) {
      if (item && typeof item === "object") {
        return item.value;
      }

      return item;
    },
    getWorkingCountryFlag(item) {
      switch (this.getWorkingCountryValue(item)) {
        case "PAK":
          return require("../../assets/pakflag.png");
        case "KSA":
          return require("../../assets/ksaflag.png");
        case "UAE":
        case "UAE LA":
          return require("../../assets/Uaeflag.png");
        case "OMAN":
          return require("../../assets/omanflag.png");
        default:
          return null;
      }
    },
    isWorkingCountrySelected(item) {
      return (
        this.selectedCountry.indexOf(this.getWorkingCountryValue(item)) >= 0
      );
    },
    mixpanel() {
      this.track("Analytics", {
        $email: localStorage.getItem("email"),
        $name: localStorage.getItem("user"),
        WorkingCountry: localStorage.getItem("workingCountry"),
      });
    },
    socketEvent(permission, eventEmitTo, data) {
      let operationName = permission;
      let userPermissions = JSON.parse(
        localStorage.getItem("permissions")
      ).permissions;
      userPermissions.filter((x) => {
        if (x.operationName == operationName) {
          this.$eventBus.$emit(eventEmitTo, data);

          if (
            permission != "view-sales-invoice" ||
            permission != "view-purchase-invoice" ||
            permission != "generate-downaload-so-si-for-accounts" ||
            permission != "generate-downaload-po-pi-for-accounts"
          ) {
            if (data.workingCountry.indexOf("UAE") >= 0) {
              this.jobCommentsUEA = this.jobCommentsUEA + 1;
              this.getUnreadNotificationCount();
              this.$eventBus.$emit("notify");
            }
            if (data.workingCountry.indexOf("PAK") >= 0) {
              this.jobCommentsPAK = this.jobCommentsPAK + 1;
              this.getUnreadNotificationCount();
              this.$eventBus.$emit("notify");
            }
            if (data.workingCountry.indexOf("KSA") >= 0) {
              this.jobCommentsKSA = this.jobCommentsKSA + 1;
              this.getUnreadNotificationCount();
              this.$eventBus.$emit("notify");
            }
            if (data.workingCountry.indexOf("UAE LA") >= 0) {
              this.jobCommentsUAELA = this.jobCommentsUAELA + 1;
              this.getUnreadNotificationCount();
              this.$eventBus.$emit("notify");
            }
          }
        }
      });
    },

    socketEventRoleBased(permission, eventEmitTo, data) {
      let roles = JSON.parse(localStorage.getItem("permissions")).roles;
      roles = roles.map((x) => x.role.roleName.toLowerCase());
      if (roles.includes(permission)) {
        eventEmitTo ? this.$eventBus.$emit(eventEmitTo, data) : "";
        if (data.workingCountry.indexOf("UAE") >= 0) {
          this.jobCommentsUEA = this.jobCommentsUEA + 1;
          this.getUnreadNotificationCount();
          this.$eventBus.$emit("notify");
        }
        if (data.workingCountry.indexOf("PAK") >= 0) {
          this.jobCommentsPAK = this.jobCommentsPAK + 1;
          this.getUnreadNotificationCount();
          this.$eventBus.$emit("notify");
        }
        if (data.workingCountry.indexOf("KSA") >= 0) {
          this.jobCommentsKSA = this.jobCommentsKSA + 1;
          this.getUnreadNotificationCount();
          this.$eventBus.$emit("notify");
        }
        if (data.workingCountry.indexOf("UAE LA") >= 0) {
          this.jobCommentsUAELA = this.jobCommentsUAELA + 1;
          this.getUnreadNotificationCount();
          this.$eventBus.$emit("notify");
        }
      }
    },

    // socketFullData(permission, eventEmitTo, data) {
    //   let roles = JSON.parse(localStorage.getItem("permissions")).roles;
    //   roles = roles.map((x) => x.role.roleName.toLowerCase());
    //   if (roles.includes(permission)) {
    //     eventEmitTo ? this.$eventBus.$emit(eventEmitTo, data) : "";
    //     if (data.workingCountry.indexOf("UAE") >= 0) {
    //       this.jobCommentsUEA = this.jobCommentsUEA + 1;
    //       this.getUnreadNotificationCount();
    //       this.$eventBus.$emit("notify");
    //     }
    //     if (data.workingCountry.indexOf("PAK") >= 0) {
    //       this.jobCommentsPAK = this.jobCommentsPAK + 1;
    //       this.getUnreadNotificationCount();
    //       this.$eventBus.$emit("notify");
    //     }
    //     if (data.workingCountry.indexOf("KSA") >= 0) {
    //       this.jobCommentsKSA = this.jobCommentsKSA + 1;
    //       this.getUnreadNotificationCount();
    //       this.$eventBus.$emit("notify");
    //     }
    //   }
    // },

    checkPermissionMapping() {
      const { serverUrl, rbacUrl, apiUrl } = this.constants;
      this.$http
        .post(`${rbacUrl}/checkUserPermission`, {
          userId: localStorage.getItem("userId"),
        })
        .then((res) => {
          if (!res.data.data) {
            this.loading = false;
          }
          let old = res.data.data.roles;
          old = old.map((x) => x.role.roleName.toLowerCase());
          let roles = JSON.parse(localStorage.getItem("permissions")).roles;
          roles = roles.map((x) => x.role.roleName.toLowerCase());
          old.sort();
          roles.sort();
          if (old.toString() != roles.toString()) {
            this.showScreen = false;
            localStorage.setItem("permissions", JSON.stringify(res.data.data));
            Swal.fire({
              title: "Oops!",
              text: "Your permissions have changed .",
              type: "error",
              allowOutsideClick: false,
              allowEscapeKey: false,
              confirmButtonColor: "orange",
              confirmButtonText: "Okay",
            }).then((result) => {
              localStorage.setItem("first-load", true);
              this.$router.replace("/");
              location.reload();
            });
          }
          if (old.toString() == roles.toString()) {
            localStorage.setItem("permissions", JSON.stringify(res.data.data));
            window.open(
              this.constants.mappingUrl +
                "/#/redirecting-user?key=" +
                this.t +
                "&username=" +
                this.u +
                "&country=" +
                JSON.stringify(this.selectedCountry)
            );
          }
        });
    },
    notificationRead() {
      this.jobComments = 0;
      if (this.selectedCountry.indexOf("UAE") >= 0) {
        this.jobCommentsUEA = 0;
      }
      if (this.selectedCountry.indexOf("PAK") >= 0) {
        this.jobCommentsPAK = 0;
      }
      if (this.selectedCountry.indexOf("KSA") >= 0) {
        this.jobCommentsKSA = 0;
      }
      if (this.selectedCountry.indexOf("UAE LA") >= 0) {
        this.jobCommentsUAELA = 0;
      }
    },
    refresh() {
      this.newJob = 0;
      this.newInquiry = 0;
    },
    startSocket: function () {
      console.log("------socket----------------------------");
      var socket = io(this.constants.apiUrl, {
        query: "adminToken=" + localStorage.getItem(StorageKeys.SessionKey),
        forceNew: true,
      });

      socket.on("connect", () => {});
      socket.on("1", (data) => {
        cosnole.log("advanceRequestttttttt");
        this.socketEventRoleBased("accounts", "finance", data);
        if (this.selectedCountry.includes(data.workingCountry)) {
          new Audio(
            require("../../assets/elevator-ding.mp3")
          ).play();
        }
      });
      socket.on("salesInvoice", (data) => {
        console.log("salesInvoice", data);
        console.log("-------------------salesInvoice------------");
        if (this.$route.name == "Accounts" || this.$route.name == "Advance") {
          this.socketEvent(
            "generate-downaload-so-si-for-accounts",
            "accounts-sales-invoice",
            data
          );
        } else {
          this.socketEvent(
            "view-sales-invoice",
            "job-details-sales-invoice",
            data
          );
        }
      });
      socket.on("salesOrder", (data) => {
        console.log("salesOrder", data);
        console.log("-------------------salesOrder------------");
        if (this.$route.name == "Accounts" || this.$route.name == "Advance") {
          this.socketEvent(
            "generate-downaload-so-si-for-accounts",
            "accounts-sales-order",
            data
          );
        } else {
          this.socketEvent(
            "view-sales-invoice",
            "job-details-sales-order",
            data
          );
        }
      });
      socket.on("purchaseOrder", (data) => {
        console.log("-------------------purchaseOrder------------", data);
        if (this.$route.name == "Accounts" || this.$route.name == "Advance") {
          this.socketEvent(
            "generate-downaload-po-pi-for-accounts",
            "accounts-purchase-order",
            data
          );
        } else {
          this.socketEvent(
            "view-purchase-invoice",
            "job-details-purchase-order",
            data
          );
        }
      });
      socket.on("vasPak", (data) => {
        this.socketEvent("view-purchase-invoice", "VASPAK", data);
      });
      socket.on("changeMilestone", (data) => {
        console.log(data);
        this.socketEvent("change-milestone", "refresh-milstoneChange", data);
      });

      socket.on("togglePurchaseInvoiceSwitch", (data) => {
        this.socketEvent(
          "view-purchase-invoice",
          "togglePurchaseInvoiceSwitch",
          data
        );
      });
      socket.on("purchaseInvoice", (data) => {
        console.log("-------------------purchaseInvoice------------", data);
        if (this.$route.name == "Accounts" || this.$route.name == "Advance") {
          this.socketEvent(
            "generate-downaload-po-pi-for-accounts",
            "accounts-purchase-invoice",
            data
          );
        } else {
          this.socketEvent(
            "view-purchase-invoice",
            "job-details-purchase-invoice",
            data
          );
        }
      });
      socket.on("assignment-salesInvoice", (data) => {
        console.log("----------sales--assignment_Invoice", data);
        if (this.$route.name == "Accounts" || this.$route.name == "Advance") {
          this.socketEvent(
            "generate-downaload-po-pi-for-accounts",
            "accounts-purchase-invoice2",
            data
          );
        } else {
          this.socketEvent(
            "generate-downaload-po-pi-for-accounts",
            "sales-invoice-assignment",
            data
          );
        }
      });
      // socket.on("customerRecipt", (data) => {
      //   console.log("customerreciptedit", data);
      //   this.socketEvent("collect-gps", "customerRecipt", data);
      // });
      socket.on("IBUSERUPDATE", (data) => {
        console.log(data);
        this.socketEvent("collect-gps", "Ibupdate", data);
      });

      socket.on("jobApprovalPending", (data) => {
        this.socketEvent("job-approval", "job-approval-notification", data);
        new Audio(
          require("../../assets/elevator-ding.mp3")
        ).play();
      });
      socket.on("customerRecipt", (data) => {
        console.log("customerreciptedit", data);
        this.socketEvent("collect-gps", "customerRecipt", data);
      });
      socket.on("inquryApprovalPending", (data) => {
        this.socketEvent("sales-approval", "sales-approval-notification", data);
        new Audio(
          require("../../assets/elevator-ding.mp3")
        ).play();
      });
      socket.on("collectGpsDevice", (data) => {
        this.socketEvent("collect-gps", "imei-section-collect-gps", data);
      });
      socket.on("Additionalcharge", (data) => {
        console.log("additionalCharge", data);
        // this.socketEventRoleBased("accounts", "additional-charge-update", data);
        this.socketEvent(
          "view-additional-charge-name",
          "additionalchargeUpdate",
          data
        );
      });
      socket.on("comments", (data) => {
        // let operationName = "job-comments";
        this.socketEvent("job-comments", "new-sales-comment", data);

        if (this.selectedCountry.includes(data.workingCountry)) {
          new Audio(
            require("../../assets/elevator-ding.mp3")
          ).play();
        }
      });

      socket.on("bookingFromCustomer", (data) => {
        this.socketEventRoleBased("procurements", "procurement", data);
        if (this.selectedCountry.includes(data.workingCountry)) {
          new Audio(
            require("../../assets/elevator-ding.mp3")
          ).play();
        }
      });
      socket.on("newCustomer", (data) => {
        console.log("nwCustomer", data);
        this.socketEventRoleBased("customers", "customers", data);
        if (this.selectedCountry.includes(data.workingCountry)) {
          new Audio(
            require("../../assets/elevator-ding.mp3")
          ).play();
        }
      });
      // add new driver socket
      socket.on("addDriver", (data) => {
        this.socketEventRoleBased("drivers", "drivers", data);
        if (this.selectedCountry.includes(data.workingCountry)) {
          new Audio(
            require("../../assets/elevator-ding.mp3")
          ).play();
        }
      });
      socket.on("callbackFromCustomer", (data) => {
        this.socketEventRoleBased("request-callback", "requestCallback", data);
        if (this.selectedCountry.includes(data.workingCountry)) {
          new Audio(
            require("../../assets/elevator-ding.mp3")
          ).play();
        }
      });
      socket.on("customerCreated", (data) => {
        console.log("----------------------------------------------------");
        this.socketEventRoleBased("customers", "customers", data);

        if (this.selectedCountry.includes(data.workingCountry)) {
          new Audio(
            require("../../assets/elevator-ding.mp3")
          ).play();
        }
      });
      socket.on("inquiryCreated", (data) => {
        console.log("----------------inquirycreated----------------");
        this.socketEventRoleBased("sales", "Sales", data);

        if (this.selectedCountry.includes(data.workingCountry)) {
          new Audio(
            require("../../assets/elevator-ding.mp3")
          ).play();
        }
      });
      socket.on("gpsCollectedSocket", (data) => {
        this.socketEventRoleBased("accounts", null, data);
        if (this.selectedCountry.includes(data.workingCountry)) {
          new Audio(
            require("../../assets/elevator-ding.mp3")
          ).play();
        }
      });
      socket.on("notificationDriverPayment", (data) => {
        this.socketEventRoleBased("accounts", null, data);
        if (this.selectedCountry.includes(data.workingCountry)) {
          new Audio(
            require("../../assets/elevator-ding.mp3")
          ).play();
        }
      });
      socket.on("rejectAdvance", (data) => {
        this.socketEventRoleBased("accounts", "finance", data);
        if (this.selectedCountry.includes(data.workingCountry)) {
          new Audio(
            require("../../assets/elevator-ding.mp3")
          ).play();
        }
      });
      socket.on("issueAdvance", (data) => {
        console.log("issueAdvance");
        this.socketEventRoleBased("accounts", "finance", data);
        if (this.selectedCountry.includes(data.workingCountry)) {
          new Audio(
            require("../../assets/elevator-ding.mp3")
          ).play();
        }
      });

      socket.on("advancePaidPush", (data) => {
        console.log("-------------advancePAid");
        this.socketEventRoleBased("accounts", "assignments", data);
        this.socketEventRoleBased("accounts", "subpartiallist", data);

        if (this.selectedCountry.includes(data.workingCountry)) {
          new Audio(
            require("../../assets/elevator-ding.mp3")
          ).play();
        }
      });
      socket.on("DocReceivedByAccounts", (data) => {
        console.log("-------------advancePAid");
        this.socketEventRoleBased("accounts", "assignments", data);
        if (this.selectedCountry.includes(data.workingCountry)) {
          new Audio(
            require("../../assets/elevator-ding.mp3")
          ).play();
        }
      });
      socket.on("paymentDone", (data) => {
        console.log("paymentDone");
        console.log(data);
        this.socketEventRoleBased("accounts", "assignments", data);

        if (this.selectedCountry.includes(data.workingCountry)) {
          new Audio(
            require("../../assets/elevator-ding.mp3")
          ).play();
        }
      });
      socket.on("readyToPayFinalPayment", (data) => {
        console.log("-----readyToPayFinalPayment");
        this.socketEventRoleBased("accounts", "finance", data);
        if (this.selectedCountry.includes(data.workingCountry)) {
          new Audio(
            require("../../assets/elevator-ding.mp3")
          ).play();
        }
      });

      socket.on("additionalCharges", (data) => {
        console.log("---additionalCharges-----------", data);
        setTimeout(() => {
          this.socketEventRoleBased("accounts", "additionalchargeAdd", data);
        }, 3000);

        if (this.selectedCountry.includes(data.workingCountry)) {
          new Audio(
            require("../../assets/elevator-ding.mp3")
          ).play();
        }
      });
      socket.on("readyToPay", (data) => {
        console.log("-----readyToPay-----");
        this.socketEventRoleBased("accounts", "finance", data);
        if (this.selectedCountry.includes(data.workingCountry)) {
          new Audio(
            require("../../assets/elevator-ding.mp3")
          ).play();
        }
      });
      socket.on("cancelAdvance", (data) => {
        console.log("----canceladvance");
        this.socketEventRoleBased("accounts", "finance", data);
        if (this.selectedCountry.includes(data.workingCountry)) {
          new Audio(
            require("../../assets/elevator-ding.mp3")
          ).play();
        }
      });
      socket.on("RejectedAdvance", (data) => {
        console.log("----RejectedAdvance");
        this.socketEventRoleBased("jobs", "assignments", data);
        if (this.selectedCountry.includes(data.workingCountry)) {
          new Audio(
            require("../../assets/elevator-ding.mp3")
          ).play();
        }
      });

      socket.on("driverUpdated", (data) => {
        console.log("----driverUpdated");

        this.socketEventRoleBased("transporter", "transporterDriver", data);
        if (this.selectedCountry.includes(data.workingCountry)) {
          new Audio(
            require("../../assets/elevator-ding.mp3")
          ).play();
        }
      });
      socket.on("newTransporter", (data) => {
        this.socketEventRoleBased("transporter", "newtransporters", data);
        if (this.selectedCountry.includes(data.workingCountry)) {
          new Audio(
            require("../../assets/elevator-ding.mp3")
          ).play();
        }
      });

      socket.on("driverNotAssignedYet", (data) => {
        this.socketEventRoleBased("jobs", null, data);
        if (this.selectedCountry.includes(data.workingCountry)) {
          new Audio(
            require("../../assets/elevator-ding.mp3")
          ).play();
        }
      });

      socket.on("bidPush", (data) => {
        if (data.workingCountry.indexOf("UAE") >= 0) {
          this.jobCommentsUEA = this.jobCommentsUEA + 1;
        }
        if (data.workingCountry.indexOf("PAK") >= 0) {
          this.jobCommentsPAK = this.jobCommentsPAK + 1;
        }
        if (data.workingCountry.indexOf("KSA") >= 0) {
          this.jobCommentsKSA = this.jobCommentsKSA + 1;
        }
        if (data.workingCountry.indexOf("UAE LA") >= 0) {
          this.jobCommentsUAELA = this.jobCommentsUAELA + 1;
        }
        new Audio(
          require("../../assets/elevator-ding.mp3")
        ).play();
        return;
      });

      socket.on("unassignedJob", (data) => {
        EventBus.$emit("unassignedJob-notification", data);
      });
      socket.on("assignedDriver", (data) => {
        EventBus.$emit("assignedDriver-notification", data);
      });
      socket.on("IBUSERUPDATE", (data) => {
        console.log(data);
        // EventBus.$emit("assignedDriver-notification", data);
      });
      socket.on("completeAssignment", (data) => {
        EventBus.$emit("completeAssignment-notification", data);
      });
    },
    setCountry() {
      this.error = "";
      if (!this.selectedCountry.length) {
        this.error = "Please select at least one working country";
        return;
      }
      this.workingCountries.forEach((key, index) => {
        if (this.selectedCountry.indexOf(key.value) >= 0) {
          key.isSelected = true;
        } else key.isSelected = false;
      });
      if (this.selectedCountry.length) {
        EventBus.$emit("countryChanged", this.selectedCountry);
        localStorage.setItem(
          "workingCountries",
          JSON.stringify(this.workingCountries)
        );
        this.checkPermission();
        //  this.$emit("notification-List")
        this.getUnreadNotificationCount();
      }
    },
    internet() {
      if (!navigator.onLine) {
        this.loading = false;
        this.error = "Please check your internet connection";
        setTimeout(() => {
          this.error = "";
        }, 3000);
        return;
      }
    },
    open(title, message, options) {
      this.dialog = true;
      this.title = "Do You Want to Logout?";
      this.message = "Click Yes to Logout";
      this.options = Object.assign(this.options, options);
      return new Promise((resolve, reject) => {
        this.resolve = resolve;
        this.reject = reject;
      });
    },
    agree() {
      this.resolve(true);
      this.logout();
      this.dialog = false;
    },
    cancel() {
      this.resolve(false);
      this.dialog = false;
    },
    selectActive(index) {
      localStorage.setItem("activeMenu", index);
      this.index = Number(localStorage.getItem("activeMenu"));
      if (localStorage.getItem("workingCountries")) {
        this.workingCountries = JSON.parse(
          localStorage.getItem("workingCountries")
        );
        this.selectedCountry = [];
        this.workingCountries.forEach((key, index) => {
          if (key.isSelected) {
            this.selectedCountry.push(key.value);
          }
        });
      }
    },
    async getUnreadNotificationCount() {
      if (localStorage.getItem("workingCountries")) {
        this.workingCountries = JSON.parse(
          localStorage.getItem("workingCountries")
        );
        let workingCountry = [];
        this.workingCountries.forEach((key, index) => {
          if (key.isSelected) {
            workingCountry.push(key.value);
          }
        });
        this.workingCountry = JSON.stringify(workingCountry);
      }
      let url = `/commentRoute/v1/getNotificationCount?workingCountry=${this.workingCountry}`;
      delete this.axios.defaults.headers.common["token"];
      let token = localStorage.getItem(StorageKeys.SessionKey);
      let config = {
        headers: {
          authorization: `bearer ${token}`,
        },
      };
      const response = await this.axios.get(
        this.constants.apiUrl + url,
        config
      );
      if (response.status === 200) {
        this.unreadCount = response.data.data.totalCount;
        localStorage.setItem("unreadCount", this.unreadCount);
      } else {
        this.loading = false;
      }
    },
    goHome() {
      this.index = 2;
      localStorage.setItem("activeMenu", this.index);
      this.$router.push("/jobs");
    },
    goBackFromTopbar() {
      if (this.opsEntryTopbar && this.opsEntryTopbar.backPath) {
        this.$router.push(this.opsEntryTopbar.backPath);
        return;
      }
      if (window.history.length > 1) {
        this.$router.go(-1);
        return;
      }
      const fallback =
        (this.opsEntryTopbar && this.opsEntryTopbar.fallbackPath) || "/jobs";
      this.$router.push(fallback);
    },
    logout() {
      const _parentScope = this;
      let url = "/dmsAdmin/logout";
      delete this.axios.defaults.headers.common["token"];
      let token = localStorage.getItem(StorageKeys.SessionKey);
      let config = {
        headers: {
          authorization: `bearer ${token}`,
        },
      };
      let body = {};

      this.axios.post(this.constants.apiUrl + url, {}, config).then(
        (response) => {
          this.index = 2;
          this.resetId();
          import("../../constants/constants.js").then(({ StorageKeys }) => {
            this.removeStorage("clearAll");
            localStorage.removeItem(StorageKeys.SessionKey);
            _parentScope.$router.replace("/auth");
          });
        },
        (error) => {
          this.error = "Failed to Logout";
        }
      );
    },
  },
};
</script>

<style>
#nav {
  background: var(--oms-bg, #f6f5f2);
  margin-left: 14.75rem;
  margin-top: 3.75rem;
  min-height: calc(100vh - 3.75rem);
  overflow-x: hidden;
  transition: margin-left 0.22s ease, width 0.22s ease;
  width: calc(100% - 14.75rem);
}

#nav.auth-route-shell {
  margin-left: 0;
  margin-top: 0;
  width: 100%;
}

.oms-shell-chrome {
  --oms-sidebar-width: 14.75rem;
  --oms-topbar-height: 3.75rem;
  color: var(--oms-ink, #3a382f);
}

html.oms-sidebar-collapsed #nav {
  margin-left: 4.875rem;
  width: calc(100% - 4.875rem);
}

html.oms-sidebar-collapsed .oms-shell-chrome {
  --oms-sidebar-width: 4.875rem;
}

.oms-sidebar {
  background:
    linear-gradient(180deg, #173399, #122878),
    #122878;
  border-right: 1px solid rgba(255, 255, 255, 0.16);
  bottom: 0;
  box-shadow: 18px 0 45px rgba(8, 16, 56, 0.22);
  color: #ffffff;
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
  left: 0;
  overflow-x: hidden;
  overflow-y: auto;
  padding: 1rem 0.875rem;
  position: fixed;
  top: 0;
  transition: width 0.22s ease, padding 0.22s ease;
  width: var(--oms-sidebar-width);
  z-index: 30;
}

.oms-sidebar__brand {
  align-items: flex-start;
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
  margin-bottom: 0.125rem;
  min-width: 0;
  padding: 0.625rem 0.5rem 0.75rem;
  width: 100%;
}

.oms-sidebar__brand-row {
  align-items: center;
  display: flex;
  gap: 0.75rem;
  justify-content: space-between;
  min-width: 0;
  width: 100%;
}

.oms-brand-link {
  align-items: center;
  background: transparent;
  border: 0;
  border-radius: 0;
  box-shadow: none;
  display: inline-flex;
  flex: 1 1 auto;
  height: 3.875rem;
  justify-content: flex-start;
  max-width: 8.75rem;
  min-width: 0;
  padding: 0;
}

.oms-brand-link img {
  display: block;
  height: auto;
  max-height: 3.875rem;
  object-fit: contain;
  width: 100%;
}

.oms-brand-copy {
  display: flex;
  flex-direction: column;
  line-height: 1.1;
  min-width: 0;
}

.oms-sidebar-toggle.v-btn {
  align-items: center;
  background: #ffffff !important;
  border: 1px solid rgba(255, 255, 255, 0.56) !important;
  border-radius: 999px !important;
  box-sizing: border-box;
  box-shadow: 0 10px 24px rgba(8, 16, 56, 0.22) !important;
  color: #173399 !important;
  display: inline-flex;
  flex: 0 0 auto;
  height: 2rem !important;
  justify-content: center;
  margin: 0 !important;
  min-width: 2rem !important;
  overflow: hidden;
  padding: 0 !important;
  position: relative;
  transform: none !important;
  transition:
    background 0.16s ease,
    border-color 0.16s ease,
    color 0.16s ease;
  width: 2rem !important;
  z-index: 1;
}

.oms-sidebar-toggle.v-btn::before {
  display: none !important;
}

.oms-sidebar-toggle.v-btn:hover,
.oms-sidebar-toggle.v-btn:focus {
  background: #f8bd82 !important;
  border-color: rgba(248, 189, 130, 0.92) !important;
  box-shadow: 0 10px 24px rgba(8, 16, 56, 0.22) !important;
  transform: none !important;
}

.oms-sidebar-toggle.v-btn .v-btn__content {
  height: 100%;
  justify-content: center;
  width: 100%;
}

.oms-sidebar-toggle.v-btn .v-icon {
  color: #173399 !important;
  font-size: 1.125rem;
}

.oms-sidebar-toggle.v-btn:hover .v-icon,
.oms-sidebar-toggle.v-btn:focus .v-icon {
  color: #122878 !important;
}

.oms-brand-copy span,
.oms-sidebar__section-label {
  color: #ffffff;
  font-family: "Geist Mono", ui-monospace, SFMono-Regular, Consolas, monospace;
  font-size: 0.625rem;
  font-weight: 600;
  letter-spacing: 0.24em;
  text-transform: uppercase;
}

.oms-sidebar__section-label {
  color: #aeb8e6;
  font-family: "Geist", system-ui, -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif;
  letter-spacing: 0.08em;
  margin: 0.5rem 0.375rem 0;
}

.oms-sidebar__nav {
  display: flex;
  flex-direction: column;
  gap: 0.1875rem;
}

.oms-side-nav-btn.v-btn {
  background: transparent !important;
  border: 1px solid transparent !important;
  border-radius: 9px !important;
  box-shadow: none !important;
  color: #c3cbed !important;
  height: 2.375rem !important;
  justify-content: flex-start;
  margin: 0 !important;
  min-width: 0 !important;
  overflow: visible;
  padding: 0 0.6875rem !important;
  position: relative;
  text-transform: none !important;
  width: 100%;
}

.oms-side-nav-btn.v-btn .v-btn__content {
  align-items: center;
  color: inherit !important;
  display: flex;
  font-size: 0.84375rem;
  font-weight: 600;
  gap: 0.6875rem;
  justify-content: flex-start;
  letter-spacing: 0;
  width: 100%;
}

.oms-side-nav-btn.v-btn .v-icon {
  color: #ea7a1c !important;
  flex: 0 0 auto;
  font-size: 1.0625rem;
}

.oms-side-nav-btn.v-btn:hover,
.oms-side-nav-btn--active.v-btn {
  background: rgba(255, 255, 255, 0.16) !important;
  border-color: rgba(255, 255, 255, 0.12) !important;
  color: #ffffff !important;
}

.oms-side-nav-btn--active.v-btn::before {
  background: #ea7a1c;
  border-radius: 0 3px 3px 0;
  bottom: 0.5rem;
  content: "";
  left: -0.875rem;
  opacity: 1;
  position: absolute;
  top: 0.5rem;
  width: 0.1875rem;
}

.oms-sidebar__spacer {
  flex: 1 1 auto;
}

.oms-sidebar__user {
  align-items: center;
  display: flex;
  gap: 0.625rem;
  margin-top: 1rem;
  min-width: 0;
  padding: 0.25rem 0.375rem;
  width: 100%;
}

.oms-sidebar__user-avatar {
  align-items: center;
  background: #ea7a1c;
  border-radius: 9px;
  color: #122878;
  display: inline-flex;
  flex: 0 0 2rem;
  font-size: 0.75rem;
  font-weight: 800;
  height: 2rem;
  justify-content: center;
  letter-spacing: 0;
  width: 2rem;
}

.oms-sidebar__user-copy {
  display: flex;
  flex: 1 1 auto;
  flex-direction: column;
  line-height: 1.15;
  min-width: 0;
}

.oms-sidebar__user-name,
.oms-sidebar__user-role {
  display: block;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.oms-sidebar__user-name {
  color: #ffffff;
  font-size: 0.78125rem;
  font-weight: 700;
}

.oms-sidebar__user-role {
  color: #aeb8e6;
  font-size: 0.65625rem;
  font-weight: 600;
  margin-top: 0.125rem;
}

.oms-sidebar__country {
  align-items: center;
  background: rgba(255, 255, 255, 0.1);
  border: 1px solid rgba(255, 255, 255, 0.2);
  border-radius: 9px;
  display: flex;
  min-height: 2.375rem;
  overflow: visible;
  position: relative;
  width: 100%;
}

.oms-main-topbar {
  align-items: center;
  backdrop-filter: blur(8px);
  -webkit-backdrop-filter: blur(8px);
  background: rgba(246, 245, 242, 0.85);
  border-bottom: 1px solid #e7e5df;
  display: flex;
  gap: 0.75rem;
  height: 3.75rem;
  justify-content: flex-end;
  left: var(--oms-sidebar-width);
  padding: 0 1.5rem;
  position: fixed;
  right: 0;
  top: 0;
  transition: left 0.22s ease;
  z-index: 24;
}

.oms-main-topbar--page-context {
  justify-content: space-between;
}

.oms-topbar-page-context {
  align-items: center;
  display: flex;
  flex: 1 1 auto;
  gap: 0.75rem;
  min-width: 0;
  padding-right: 1rem;
}

.oms-topbar-back.v-btn {
  background: #ffffff !important;
  border: 1px solid #e4e1d9 !important;
  border-radius: 0.625rem !important;
  box-shadow: none !important;
  flex: 0 0 auto;
  height: 2.5rem !important;
  margin: 0 !important;
  min-width: 2.5rem !important;
  width: 2.5rem !important;
}

.oms-topbar-back.v-btn .v-icon {
  color: #6b6860 !important;
  font-size: 1.125rem;
}

.oms-topbar-back.v-btn:hover,
.oms-topbar-back.v-btn:focus {
  border-color: #ea7a1c !important;
}

.oms-topbar-back.v-btn:hover .v-icon,
.oms-topbar-back.v-btn:focus .v-icon {
  color: #d96a10 !important;
}

.oms-topbar-page-copy {
  display: flex;
  flex-direction: column;
  gap: 0.125rem;
  min-width: 0;
}

.oms-topbar-page-title {
  color: #1a1916;
  font-size: 1rem;
  font-weight: 800;
  line-height: 1.2;
  white-space: nowrap;
}

.oms-topbar-page-subtitle {
  color: #6b6860;
  font-size: 0.75rem;
  font-weight: 500;
  line-height: 1.2;
  max-width: 32rem;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.oms-country-select.country-menu-header {
  background: transparent;
  border: 0;
  border-radius: 9px;
  margin: 0 !important;
  padding: 0 !important;
  pointer-events: auto;
  width: 100%;
}

.oms-country-select.country-menu-header.v-text-field {
  margin-top: 0 !important;
  padding-top: 0 !important;
}

.oms-country-select.country-menu-header .v-input__control {
  height: 2.375rem !important;
  min-height: 2.375rem !important;
}

.oms-country-select.country-menu-header .v-input__slot {
  background: transparent !important;
  border: 0 !important;
  box-shadow: none !important;
  margin: 0 !important;
  min-height: 2.375rem !important;
  padding: 0 0.375rem 0 0.75rem !important;
  pointer-events: auto;
}

.oms-country-select.country-menu-header .v-select__slot {
  align-items: center;
  display: flex;
  min-height: 2.375rem;
  width: 100%;
}

.oms-country-select.country-menu-header .v-input__slot::before,
.oms-country-select.country-menu-header .v-input__slot::after {
  display: none !important;
}

.oms-country-select.country-menu-header input,
.oms-country-select.country-menu-header .v-label,
.oms-country-select.country-menu-header .v-select__selection {
  color: #ffffff !important;
  font-size: 0.875rem;
  font-weight: 600;
}

.oms-country-select.country-menu-header .v-select__selections {
  align-items: center;
  flex-wrap: nowrap;
  min-height: 2.375rem !important;
  overflow: hidden;
  padding: 0 !important;
}

.oms-country-select.country-menu-header .v-select__selection {
  max-width: 7.25rem;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.oms-country-selection {
  align-items: center;
  color: #ffffff;
  display: inline-flex;
  font-size: 0.875rem;
  font-weight: 600;
  gap: 0.4rem;
  max-width: 8.25rem;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.oms-country-flag {
  border-radius: 0.1875rem;
  display: inline-block;
  flex: 0 0 auto;
  height: 1rem;
  object-fit: cover;
  vertical-align: middle;
  width: 1.35rem;
}

.oms-country-flag--selection {
  height: 0.9rem;
  width: 1.2rem;
}

.oms-country-flag--round {
  border-radius: 50%;
  height: 1rem;
  object-fit: cover;
  width: 1rem;
}

.oms-country-select.country-menu-header .v-input__append-inner {
  align-self: center;
  cursor: pointer;
  margin-top: 0 !important;
}

.oms-country-select.country-menu-header .v-input__append-inner .v-input__icon {
  cursor: pointer;
}

.oms-country-select.country-menu-header .v-input__append-inner .v-icon {
  color: rgba(255, 255, 255, 0.86) !important;
  pointer-events: auto;
}

.oms-country-select.country-menu-header .v-text-field__details {
  display: none !important;
}

.application .oms-country-menu.v-menu__content,
.oms-country-menu.v-menu__content {
  background:
    linear-gradient(
      180deg,
      rgba(23, 51, 153, 0.9),
      rgba(18, 40, 120, 0.84)
    ),
    rgba(18, 40, 120, 0.86) !important;
  backdrop-filter: blur(10px);
  border: 1px solid rgba(255, 255, 255, 0.18);
  border-radius: 12px;
  box-shadow: 0 18px 45px rgba(8, 16, 56, 0.28);
  margin-top: 0.375rem;
  min-width: 12.5rem !important;
  overflow: hidden;
  transform-origin: top center;
  z-index: 80 !important;
}

.application .oms-country-menu .theme--light.v-list,
.application .oms-country-menu .v-list,
.oms-country-menu .theme--light.v-list,
.oms-country-menu .v-list {
  background: transparent !important;
  color: #eaf0ff !important;
  padding: 0.375rem;
}

.application .oms-country-menu .theme--light.v-list .v-list__tile,
.application .oms-country-menu .v-list__tile,
.oms-country-menu .theme--light.v-list .v-list__tile,
.oms-country-menu .v-list__tile {
  background: transparent !important;
  border-radius: 9px;
  color: #eaf0ff !important;
  min-height: 2.25rem;
  padding: 0 0.625rem;
}

.application .oms-country-menu .v-list__tile__action,
.oms-country-menu .v-list__tile__action {
  min-width: 2rem;
}

.application .oms-country-menu .v-input--selection-controls,
.oms-country-menu .v-input--selection-controls {
  margin: 0;
  padding: 0;
}

.application .oms-country-menu .v-input--selection-controls__input,
.oms-country-menu .v-input--selection-controls__input {
  align-items: center;
  background: rgba(255, 255, 255, 0.08);
  border: 1px solid rgba(255, 255, 255, 0.28);
  border-radius: 6px;
  box-sizing: border-box;
  height: 1.25rem;
  justify-content: center;
  margin-right: 0.625rem;
  width: 1.25rem;
}

.application .oms-country-menu .v-list__tile__title,
.application .oms-country-menu .theme--light.v-list .v-list__tile__title,
.oms-country-menu .v-list__tile__title,
.oms-country-menu .theme--light.v-list .v-list__tile__title {
  color: #eaf0ff !important;
  font-size: 0.8125rem;
  font-weight: 700;
  line-height: 1.2;
}

.application .oms-country-menu .v-list__tile:hover,
.application .oms-country-menu .v-list__tile--highlighted,
.oms-country-menu .v-list__tile:hover,
.oms-country-menu .v-list__tile--highlighted {
  background: rgba(255, 255, 255, 0.12) !important;
}

.application .oms-country-menu .v-list__tile--active,
.oms-country-menu .v-list__tile--active {
  background: rgba(234, 122, 28, 0.18) !important;
  color: #f8bd82 !important;
}

.application .oms-country-menu .v-list__tile--active .v-list__tile__title,
.oms-country-menu .v-list__tile--active .v-list__tile__title {
  color: #f8bd82 !important;
}

.application .oms-country-menu .v-input--selection-controls__input .v-icon,
.oms-country-menu .v-input--selection-controls__input .v-icon {
  color: #f8bd82 !important;
  font-size: 1rem;
}

.application .oms-country-menu .accent--text,
.application .oms-country-menu .primary--text,
.application .oms-country-menu .v-input--is-label-active .v-icon,
.oms-country-menu .accent--text,
.oms-country-menu .primary--text,
.oms-country-menu .v-input--is-label-active .v-icon {
  color: #f8bd82 !important;
}

.application .oms-country-menu .v-input--is-label-active .v-input--selection-controls__input,
.oms-country-menu .v-input--is-label-active .v-input--selection-controls__input {
  background: rgba(234, 122, 28, 0.22);
  border-color: #f8bd82;
}

.application .oms-country-menu .v-input--selection-controls__ripple,
.oms-country-menu .v-input--selection-controls__ripple {
  color: rgba(234, 122, 28, 0.18) !important;
}

.oms-sidebar__country .v-menu__content,
.oms-country-select--sidebar .v-menu__content {
  background:
    linear-gradient(
      180deg,
      rgba(23, 51, 153, 0.9),
      rgba(18, 40, 120, 0.84)
    ),
    rgba(18, 40, 120, 0.86) !important;
  backdrop-filter: blur(10px);
  border: 1px solid rgba(255, 255, 255, 0.18) !important;
  border-radius: 12px;
  box-shadow: 0 18px 45px rgba(8, 16, 56, 0.28);
  overflow: hidden;
}

.oms-sidebar__country .v-menu__content .theme--light.v-list,
.oms-sidebar__country .v-menu__content .v-list,
.oms-country-select--sidebar .v-menu__content .theme--light.v-list,
.oms-country-select--sidebar .v-menu__content .v-list {
  background: transparent !important;
  color: #eaf0ff !important;
}

.oms-sidebar__country .v-menu__content .v-list__tile,
.oms-country-select--sidebar .v-menu__content .v-list__tile {
  background: transparent !important;
  border-radius: 9px;
  color: #eaf0ff !important;
}

.oms-sidebar__country .v-menu__content .v-list__tile__title,
.oms-country-select--sidebar .v-menu__content .v-list__tile__title {
  color: #eaf0ff !important;
  font-weight: 700;
}

.oms-sidebar__country .v-menu__content .v-list__tile:hover,
.oms-sidebar__country .v-menu__content .v-list__tile--highlighted,
.oms-country-select--sidebar .v-menu__content .v-list__tile:hover,
.oms-country-select--sidebar .v-menu__content .v-list__tile--highlighted {
  background: rgba(255, 255, 255, 0.12) !important;
}

.oms-sidebar__country .v-menu__content .v-list__tile--active,
.oms-country-select--sidebar .v-menu__content .v-list__tile--active {
  background: rgba(234, 122, 28, 0.18) !important;
}

.oms-sidebar__country .v-menu__content .v-list__tile--active .v-list__tile__title,
.oms-country-select--sidebar .v-menu__content .v-list__tile--active .v-list__tile__title {
  color: #f8bd82 !important;
}

.oms-sidebar__country .v-menu__content .v-input--selection-controls__input,
.oms-country-select--sidebar .v-menu__content .v-input--selection-controls__input {
  align-items: center;
  background: rgba(255, 255, 255, 0.08);
  border: 1px solid rgba(255, 255, 255, 0.28);
  border-radius: 6px;
  box-sizing: border-box;
  height: 1.25rem;
  justify-content: center;
  margin-right: 0.625rem;
  width: 1.25rem;
}

.oms-sidebar__country .v-menu__content .v-input--is-label-active .v-input--selection-controls__input,
.oms-country-select--sidebar .v-menu__content .v-input--is-label-active .v-input--selection-controls__input {
  background: rgba(234, 122, 28, 0.22);
  border-color: #f8bd82;
}

.oms-sidebar__country .v-menu__content .v-input--selection-controls__input .v-icon,
.oms-country-select--sidebar .v-menu__content .v-input--selection-controls__input .v-icon,
.oms-sidebar__country .v-menu__content .accent--text,
.oms-country-select--sidebar .v-menu__content .accent--text {
  color: #f8bd82 !important;
}

html.oms-sidebar-collapsed .oms-sidebar {
  align-items: center;
  padding-left: 0.625rem;
  padding-right: 0.625rem;
}

html.oms-sidebar-collapsed .oms-sidebar__brand {
  align-items: center;
  padding-left: 0;
  padding-right: 0;
  width: 100%;
}

html.oms-sidebar-collapsed .oms-sidebar__brand-row {
  flex-direction: column;
  gap: 0.625rem;
  justify-content: center;
}

html.oms-sidebar-collapsed .oms-brand-link {
  flex: 0 0 auto;
  height: 2.25rem;
  width: 2.75rem;
}

html.oms-sidebar-collapsed .oms-brand-link img {
  height: 1.75rem;
  max-width: 2.625rem;
  object-fit: contain;
  width: auto;
}

html.oms-sidebar-collapsed .oms-sidebar-toggle.v-btn {
  padding: 0 !important;
  transform: none !important;
  width: 2.125rem !important;
}

html.oms-sidebar-collapsed .oms-sidebar-toggle.v-btn:hover,
html.oms-sidebar-collapsed .oms-sidebar-toggle.v-btn:focus {
  transform: none !important;
}

html.oms-sidebar-collapsed .oms-sidebar-toggle.v-btn .v-btn__content {
  justify-content: center;
}

html.oms-sidebar-collapsed .oms-brand-copy,
html.oms-sidebar-collapsed .oms-sidebar__section-label,
html.oms-sidebar-collapsed .oms-sidebar__country,
html.oms-sidebar-collapsed .oms-sidebar__user,
html.oms-sidebar-collapsed .oms-side-nav-btn.v-btn span {
  display: none !important;
}

html.oms-sidebar-collapsed .oms-side-nav-btn.v-btn {
  justify-content: center;
  padding: 0 !important;
  width: 2.75rem;
}

html.oms-sidebar-collapsed .oms-side-nav-btn.v-btn .v-btn__content {
  justify-content: center;
}

html.oms-sidebar-collapsed .oms-sidebar__nav {
  align-items: center;
  width: 100%;
}

.oms-topbar-actions {
  align-items: center;
  display: flex;
  flex: 0 0 auto;
  gap: 0.375rem;
  margin-left: auto;
  position: relative;
}

.oms-topbar-icon-btn.v-btn {
  align-items: center;
  background: #ffffff !important;
  border: 1px solid #e4e1d9 !important;
  border-radius: 9px !important;
  box-shadow: none !important;
  color: #6b6860 !important;
  display: inline-flex;
  height: 2.375rem !important;
  justify-content: center;
  margin: 0 !important;
  min-width: 2.375rem !important;
  width: 2.375rem !important;
}

.oms-topbar-icon-btn.v-btn .v-icon {
  color: currentColor !important;
  font-size: 1rem;
}

.oms-topbar-icon-btn.v-btn:hover,
.oms-topbar-icon-btn.v-btn:focus {
  border-color: #ea7a1c !important;
  color: #d96a10 !important;
}

.oms-notification-count,
.oms-action-dot {
  align-items: center;
  background: #ea7a1c !important;
  border: 2px solid #f6f5f2;
  box-shadow: 0 6px 14px rgba(234, 122, 28, 0.28);
  display: inline-flex;
  justify-content: center;
  position: absolute;
  z-index: 7;
}

.oms-notification-count {
  left: 1.75rem;
  top: -0.3125rem;
}

.oms-action-dot {
  right: -0.25rem;
  top: -0.1875rem;
}

.oms-notification-count span {
  font-size: 0.625rem !important;
  font-weight: 800;
}

.oms-action-menu.v-list {
  background: #ffffff !important;
  border: 1px solid #e4e1d9;
  border-radius: 12px;
  box-shadow: 0 18px 45px rgba(26, 25, 22, 0.14);
  overflow: hidden;
  padding: 6px;
}

.oms-action-menu .v-list__tile {
  border-bottom: 0 !important;
  border-radius: 9px;
  color: #3a382f;
  font-weight: 700;
  min-height: 38px;
}

.oms-action-menu .v-list__tile:hover {
  background: #fdf0e4;
  color: #d96a10;
}

@media (max-width: 60rem) {
  #nav {
    margin-left: 4.875rem;
    width: calc(100% - 4.875rem);
  }

  .oms-shell-chrome {
    --oms-sidebar-width: 4.875rem;
  }

  .oms-sidebar {
    align-items: center;
    padding-left: 0.625rem;
    padding-right: 0.625rem;
  }

  .oms-sidebar__brand {
    align-items: center;
    padding-left: 0;
    padding-right: 0;
    width: 100%;
  }

  .oms-sidebar__brand-row {
    flex-direction: column;
    gap: 0.625rem;
    justify-content: center;
  }

  .oms-sidebar-toggle.v-btn {
    transform: none !important;
    width: 2.125rem !important;
  }

  .oms-brand-link {
    flex: 0 0 auto;
    height: 2.25rem;
    width: 2.75rem;
  }

  .oms-brand-link img {
    height: 1.75rem;
    max-width: 2.625rem;
    object-fit: contain;
    width: auto;
  }

  .oms-brand-copy,
  .oms-sidebar__section-label,
  .oms-sidebar__country,
  .oms-sidebar__user,
  .oms-side-nav-btn.v-btn span {
    display: none !important;
  }

  .oms-side-nav-btn.v-btn {
    justify-content: center;
    padding: 0 !important;
    width: 2.75rem;
  }

  .oms-side-nav-btn.v-btn .v-btn__content {
    justify-content: center;
  }

  .oms-main-topbar {
    padding: 0 0.875rem;
  }

  .oms-topbar-page-subtitle {
    display: none;
  }
}

@media (max-width: 45rem) {
  #nav {
    margin-left: 0;
    margin-top: 7.25rem;
    width: 100%;
  }

  .oms-shell-chrome {
    --oms-sidebar-width: 100%;
  }

  .oms-sidebar {
    bottom: auto;
    flex-direction: row;
    gap: 0.5rem;
    height: 3.625rem;
    overflow-x: auto;
    overflow-y: hidden;
    padding: 0.5rem;
    right: 0;
    width: 100%;
  }

  .oms-sidebar-toggle.v-btn {
    display: none !important;
  }

  .oms-sidebar__brand {
    margin-bottom: 0;
    padding: 0;
  }

  .oms-sidebar__brand-row {
    flex-direction: row;
    gap: 0;
    width: auto;
  }

  .oms-sidebar__brand,
  .oms-sidebar__spacer {
    flex: 0 0 auto;
  }

  .oms-sidebar__nav {
    flex-direction: row;
  }

  .oms-main-topbar {
    height: 3.625rem;
    left: 0;
    top: 3.625rem;
  }

  .oms-country-select.country-menu-header {
    flex: 1 1 auto;
    max-width: none;
  }
}

.tab {
  margin-bottom: 20px;
}
.profile-drop .v-btn {
  background-color: white !important;
  color: #424242 !important;
  box-shadow: none !important;
}
.v-toolbar__side-icon {
  border: 2px solid #ea7a1c;
  color: #ea7a1c !important;
}

.count {
  background-color: #ea7a1c;
  color: white !important;
  font-size: 14px !important;
  padding: 0 5px;
  border-radius: 5px;
  margin-left: 10px;
}
.scroll {
  height: 350px;
  overflow: auto;
}

.list-noti {
  border: 0.0625rem solid #ebe9e2;
  border-radius: 0.75rem;
  margin: 0.55rem 0;
  padding: 0;
}

.list-noti div {
  font-size: 0.8125rem;
  height: auto;
  line-height: 1.4;
}

.list-noti p {
  margin-bottom: 0;
}
.tag-orange {
  color: #ea7a1c;
  font-size: 13px;
}
.tag-gray {
  color: #b1abab;
  font-size: 12px;
}
.gray-dark {
  color: #4d4d4d;
}
.line-height-set {
  white-space: inherit !important;
}
.v-list__tile__title .line-height-set {
  white-space: inherit !important;
}
</style>
